import { ArrowRight, CheckCircle, LogOut, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useWalletConnection } from '@/hooks/useWalletConnection'
import { SiweAuthButton } from './siwe-auth-button'

type LoginStep = 'initial' | 'second-level' | 'success' | 'error'

interface LoginProcessProps {
	onSuccess?: () => void
}

const StepIndicator = ({ currentStep }: { currentStep: LoginStep }) => (
	<div className="flex items-center justify-center mb-6 space-x-4">
		{/* Step 1 - Wallet Connection */}
		<div className="flex flex-col items-center">
			<div
				className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
					currentStep === 'second-level' || currentStep === 'success'
						? 'bg-green-600 text-white'
						: currentStep === 'initial'
							? 'bg-primary text-white'
							: 'bg-gray-200 text-gray-600'
				}`}
			>
				1
			</div>
			<div className="mt-1 text-xs text-center text-gray-600">
				<div>
					<b>First Level</b>
				</div>
				<div>Authentication</div>
			</div>
		</div>

		{/* Connector line */}
		<div className="w-12 h-0.5 bg-gray-300"></div>

		{/* Step 2 - SIWE Authentication */}
		<div className="flex flex-col items-center">
			<div
				className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
					currentStep === 'success'
						? 'bg-primary text-white'
						: currentStep === 'second-level'
							? 'bg-primary text-white'
							: 'bg-gray-200 text-gray-600'
				}`}
			>
				2
			</div>
			<div className="mt-1 text-xs text-center text-gray-600">
				<div>
					<b>Second Level</b>
				</div>
				<div>Authentication</div>
			</div>
		</div>
	</div>
)
const FirstLevelStep = ({
	currentStep,
	handleStartLogin,
}: {
	currentStep: LoginStep
	handleStartLogin: () => void
}) => (
	<Card className="w-full max-w-md mx-auto">
		<CardContent className="p-8 text-center">
			<h2 className="mb-6 text-xl font-semibold">Login Process</h2>
			<StepIndicator currentStep={currentStep} />
			<p className="mb-6 text-sm text-gray-600">
				To effectively protect your account, we require you to complete a two-step
				process.
			</p>
			<Button
				onClick={handleStartLogin}
				className="w-full text-white cursor-pointer bg-primary hover:bg-seconary-200"
				size="lg"
			>
				<ArrowRight className="w-4 h-4 mr-2" />
				Start Login
			</Button>
		</CardContent>
	</Card>
)
const SecondLevelStep = ({
	currentStep,
	handleSiweSuccess,
	handleSiweError,
	handleFullLogout,
}: {
	currentStep: LoginStep
	handleSiweSuccess: () => void
	handleSiweError: () => void
	handleFullLogout: () => void
}) => (
	<Card className="w-full max-w-md mx-auto">
		<CardContent className="p-8 text-center">
			<h2 className="mb-6 text-xl font-semibold">Login Process</h2>
			<StepIndicator currentStep={currentStep} />
			<p className="mb-6 text-sm text-gray-600">
				Complete your authentication by signing the message with your wallet.
			</p>
			<div className="mb-3">
				<SiweAuthButton
					onSuccess={handleSiweSuccess}
					onError={handleSiweError}
					className="w-full"
					size="lg"
				/>
			</div>
			<div className="mb-6">
				<Button
					onClick={handleFullLogout}
					variant="destructive"
					size="lg"
					className="w-full space-y-2"
				>
					<LogOut className="w-4 h-4 mr-2" />
					Disconnect
				</Button>
			</div>
		</CardContent>
	</Card>
)
const SuccessStep = ({
	currentStep,
	onSuccess,
}: {
	currentStep: LoginStep
	onSuccess: (() => void) | undefined
}) => {
	const [countdown, setCountdown] = useState(5)

	// Handle countdown for success screen
	useEffect(() => {
		if (currentStep === 'success') {
			const timer = setInterval(() => {
				setCountdown((prev) => {
					if (prev < 1) {
						onSuccess?.()
						return 0
					}
					return prev - 1
				})
			}, 1000)

			return () => clearInterval(timer)
		}
	}, [
		currentStep,
		onSuccess,
	])

	return (
		<>
			<Card className="w-full max-w-md mx-auto">
				<CardContent className="p-8 text-center">
					<div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
						<CheckCircle className="w-8 h-8 text-green-600" />
					</div>
					<h2 className="mb-2 text-xl font-semibold">Login Successful!</h2>
					<p className="mb-4 text-sm text-gray-600">
						You have successfully authenticated. You will be redirected shortly.
					</p>
					<div className="flex items-center justify-center mb-6">
						<RefreshCw className="w-4 h-4 mr-2" />
						<span className="text-sm text-gray-600">Redirecting in {countdown} s...</span>
					</div>
					<Button
						onClick={onSuccess}
						variant="default"
						className="w-full text-white"
						size="lg"
					>
						Go to Platform
						<ArrowRight className="w-4 h-4 ml-2" />
					</Button>
				</CardContent>
			</Card>
		</>
	)
}
const ErrorStep = ({
	currentStep,
	handleSiweSuccess,
	handleSiweError,
	handleFullLogout,
}: {
	currentStep: LoginStep
	handleSiweSuccess: () => void
	handleSiweError: () => void
	handleFullLogout: () => void
}) => (
	<>
		<Card className="w-full max-w-md mx-auto">
			<CardContent className="p-8 text-center">
				<div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
					<span className="text-2xl font-bold text-red-600">!</span>
				</div>
				<h2 className="mb-2 text-xl font-semibold">Login Error</h2>
				<p className="mb-6 text-sm text-gray-600">
					An error occurred during the login process. Please try again.
				</p>
				<div className="mb-6">
					<SiweAuthButton
						onSuccess={handleSiweSuccess}
						onError={handleSiweError}
						className="w-full"
						size="lg"
						customLabel="Retry Authentication"
					/>
					<Button
						onClick={handleFullLogout}
						variant="destructive"
						size="lg"
						className="w-full mt-3"
					>
						<LogOut className="w-4 h-4 mr-2" />
						Disconnect
					</Button>
				</div>
			</CardContent>
		</Card>
		<div className="w-full px-8 md:w-50 md:px-8"></div>
	</>
)

export function LoginProcess({ onSuccess }: LoginProcessProps) {
	const [currentStep, setCurrentStep] = useState<LoginStep>('initial')
	const { isConnected, isDisconnected, handleConnect, handleFullLogout } =
		useWalletConnection()

	// Handle wallet connection state changes
	useEffect(() => {
		// Use a microtask to avoid calling setState directly in render
		if (isConnected && currentStep === 'initial') {
			Promise.resolve().then(() => handleFirstLevelSuccess())
		}
		if (isDisconnected && currentStep !== 'initial') {
			Promise.resolve().then(() => setCurrentStep('initial'))
		}
	}, [
		isConnected,
		isDisconnected,
		currentStep,
	])

	const handleStartLogin = () => {
		// Always try to connect, regardless of current state
		handleConnect()
	}

	const handleFirstLevelSuccess = () => {
		setCurrentStep('second-level')
	}

	const handleSiweSuccess = () => {
		setCurrentStep('success')
	}

	const handleSiweError = () => {
		setCurrentStep('error')
	}

	return (
		<div className="flex flex-col items-center justify-center gap-10 p-2 min-h-svh">
			<div className="flex flex-col items-center gap-4">
				<h1 className="text-3xl font-bold sm:text-4xl">GaiaDEX</h1>
			</div>
			{currentStep === 'initial' && (
				<FirstLevelStep currentStep={currentStep} handleStartLogin={handleStartLogin} />
			)}
			{currentStep === 'second-level' && (
				<SecondLevelStep
					currentStep={currentStep}
					handleSiweSuccess={handleSiweSuccess}
					handleSiweError={handleSiweError}
					handleFullLogout={handleFullLogout}
				/>
			)}
			{currentStep === 'success' && (
				<SuccessStep currentStep={currentStep} onSuccess={onSuccess} />
			)}
			{currentStep === 'error' && (
				<ErrorStep
					currentStep={currentStep}
					handleSiweSuccess={handleSiweSuccess}
					handleSiweError={handleSiweError}
					handleFullLogout={handleFullLogout}
				/>
			)}
		</div>
	)
}
